import React, { useRef, useState } from 'react';
import type { ApprovalItem } from '../client';
import AsyncButton from '../components/AsyncButton';
import { ApprovalCardHeader } from './ApprovalCardHeader';
import './approvals.css';

/**
 * Approval card for the `skill_import` type: importing an external skill whose
 * repository bundles scripts/files. Surfaces the provider trust signals (trust
 * score, risk, stars, license, repo, bundled files) so the reviewer can decide.
 */
export const SkillImportApprovalCard: React.FC<{
  approval: ApprovalItem;
  deciding?: boolean;
  onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    inputs?: Record<string, unknown>,
  ) => Promise<void>;
}> = ({ approval, deciding, onDecide }) => {
  const [note, setNote] = useState('');
  const [pendingDecision, setPendingDecision] = useState<
    'approved' | 'rejected' | null
  >(null);
  const inFlight = useRef(false);
  const d = approval.detail || {};

  const trustScore = numberOrNull(d.trustScore);
  const auditScore = numberOrNull(d.auditScore);
  const stars = numberOrNull(d.githubStars);
  const riskLevel = typeof d.riskLevel === 'string' ? d.riskLevel : null;
  const license = typeof d.license === 'string' ? d.license : null;
  const repoUrl = typeof d.repoUrl === 'string' ? d.repoUrl : null;
  const files: string[] = Array.isArray(d.files) ? (d.files as string[]) : [];
  const status = typeof d.status === 'string' ? d.status : approval.status;
  const pending = approval.status === 'pending' && status === 'pending';

  const decide = async (decision: 'approved' | 'rejected') => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPendingDecision(decision);
    try {
      await onDecide(decision, note.trim());
      setNote('');
    } finally {
      inFlight.current = false;
      setPendingDecision(null);
    }
  };

  return (
    <article
      className="approval-card approval-card-chat"
      data-testid={`approval-${approval.id}`}>
      <ApprovalCardHeader
        teamId={approval.teamId}
        agent={approval.requestingAgent}
        requestingUser={approval.requestingUser}
        requestingTrigger={approval.requestingTrigger}
        origin={approval.origin}
        createdAt={approval.createdAt}
        title={approval.title}
      />

      <div className="approval-reason">
        <span>The agent's stated reason</span>
        <p>{approval.reason}</p>
      </div>

      <div className="skill-import-signals" data-testid="skill-import-signals">
        {trustScore !== null && (
          <span className={`skill-badge ${trustBadgeClass(trustScore)}`}>
            Trust {trustScore}
          </span>
        )}
        {riskLevel && (
          <span className={`skill-badge skill-risk-${riskLevel}`}>
            Risk: {riskLevel}
          </span>
        )}
        {auditScore !== null && (
          <span className="skill-badge">Audit {auditScore}</span>
        )}
        {stars !== null && (
          <span className="skill-badge">★ {stars.toLocaleString()}</span>
        )}
        <span className="skill-badge">License: {license || 'unknown'}</span>
      </div>

      {repoUrl && (
        <p className="descriptionText" style={{ marginTop: 6 }}>
          Repository:{' '}
          <a href={repoUrl} target="_blank" rel="noreferrer">
            {repoUrl}
          </a>
        </p>
      )}

      {files.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span className="descriptionText">
            Bundled files (not imported — run only in the workspace sandbox):
          </span>
          <ul
            className="skill-import-files"
            data-testid="skill-import-files"
            style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {files.slice(0, 25).map((f) => (
              <li key={f}>
                <code>{f}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending ? (
        <>
          <label className="approval-note">
            <span>
              Note to agent <small>(optional)</small>
            </span>
            <textarea
              aria-label="Note to agent"
              maxLength={4000}
              disabled={deciding}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add context, constraints, or next steps…"
            />
          </label>
          <div className="approval-actions">
            <AsyncButton
              type="button"
              className="approval-reject"
              disabled={deciding || pendingDecision !== null}
              loading={pendingDecision === 'rejected'}
              loadingText="Rejecting..."
              onClick={() => decide('rejected')}>
              Reject
            </AsyncButton>
            <AsyncButton
              type="button"
              className="approval-approve"
              disabled={deciding || pendingDecision !== null}
              loading={pendingDecision === 'approved'}
              loadingText="Importing..."
              onClick={() => decide('approved')}>
              Approve & import
            </AsyncButton>
          </div>
        </>
      ) : (
        <div
          className={`approval-invite-status approval-invite-status-${approval.status}`}
          role="status">
          {approval.status === 'approved'
            ? 'Approved — skill imported.'
            : approval.status === 'rejected'
              ? `Rejected${approval.decisionNote ? ` — ${approval.decisionNote}` : '.'}`
              : `This request is ${approval.status}.`}
        </div>
      )}
    </article>
  );
};

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
}

function trustBadgeClass(score: number): string {
  if (score >= 85) return 'skill-badge-good';
  if (score >= 60) return 'skill-badge-warn';
  return 'skill-badge-bad';
}

export default SkillImportApprovalCard;
